/**
 * Badge — `Oprappo Design System.dc.html § 03 কাঠামো · ট্যাব · চিপ · ব্যাজ`, bottom third,
 * plus the `যাচাইকৃত` badge drawn on the § 03 card.
 *
 * Frame note: "badges are square because they are not [removable]. Status badges carry a
 * word, never colour alone — the palette must survive colour blindness." So `labelBn` is
 * required and there is no colour-only form.
 *
 * Outline badges: `border:1px solid <tone>; color:<tone>; padding:3px 8px; 500 13px/1.75`.
 *   success  #1E6B4A  সম্পন্ন / যাচাইকৃত
 *   pending  #8A5A0C  অপেক্ষমাণ
 *   danger   #B3271E  ব্যর্থ
 *
 * Count badge: `background:#B3271E; color:#fff; min-width:20px; height:20px;
 * padding:0 5px; radius:999px` — the one badge that is a pill, because it is a number,
 * not a word.
 */
import { cx } from './internal/cx.js';

export type BadgeTone = 'success' | 'pending' | 'danger';

export interface BadgeProps {
  /** Bangla word — never omit it; colour alone is not a status. */
  labelBn: string;
  tone: BadgeTone;
  /**
   * § 03's table draws its in-cell badge at `padding:2px 7px` rather than `3px 8px`.
   * Set `dense` there so the table row height matches the frame.
   */
  dense?: boolean | undefined;
  className?: string | undefined;
}

const TONE: Record<BadgeTone, string> = {
  success: 'border-success text-success',
  pending: 'border-amber-text text-amber-text',
  danger: 'border-danger text-danger',
};

export function Badge({ labelBn, tone, dense = false, className }: BadgeProps): JSX.Element {
  return (
    <span
      className={cx(
        'inline-block border font-anek font-medium text-[13px] leading-[1.75] whitespace-nowrap rounded-none',
        dense ? 'px-[7px] py-[2px]' : 'px-[8px] py-[3px]',
        TONE[tone],
        className,
      )}
    >
      {labelBn}
    </span>
  );
}

export interface CountBadgeProps {
  /**
   * The count already rendered in Bangla numerals — use `toBanglaDigits` from
   * @oprappo/tokens. Kept as a string, not a number, so the caller cannot get the
   * numeral system wrong silently.
   */
  countBn: string;
  className?: string | undefined;
}

/**
 * The design sets the count in `600 12px/1 Manrope`, but its content is `৩` — a Bangla
 * numeral, which Manrope does not carry, at a size below the 13px floor. Set here in
 * 'Anek Bangla' at 13px. The pill is 20px tall and flex-centred, so the glyph stays put.
 * See README § deviations.
 */
export function CountBadge({ countBn, className }: CountBadgeProps): JSX.Element {
  return (
    <span
      className={cx(
        'inline-flex items-center justify-center bg-danger text-white min-w-[20px] h-[20px] px-[5px] rounded-pill font-anek font-semibold text-[13px] leading-[1]',
        className,
      )}
    >
      {countBn}
    </span>
  );
}
